import { vars } from '@styles/vanilla-extract/theme.css';
import {
	HorizontalRateChartDataItem,
	HorizontalRateChartProps,
} from '@components/report/chart/horizontal-rate-chart/HorizontalRateChart';
import { SymmetricVerticalBarChartDataItem } from '@components/report/chart/symmetric-vertical-bar-chart/SymmetricVerticalBarChart';

import {
	IGAudienceGroupDefault,
	IGAudienceLanguageChild,
	IGReportRes,
	TTReportRes,
	YTReportRes,
} from '@apis/orval/model';

import { roundFloat } from '@utils/round';

import { AudienceProps } from './Audience';

export class AudienceGenderAdapter {
	value: IGReportRes | YTReportRes | TTReportRes;

	constructor(obj: IGReportRes | YTReportRes | TTReportRes) {
		this.value = obj;
	}

	get maleData() {
		return (this.value?.audience?.male_age_group ?? []).map((data) => ({
			...data,
			group_gender: 'M',
		}));
	}

	get femaleData() {
		return (this.value?.audience?.female_age_group ?? []).map((data) => ({
			...data,
			group_gender: 'F',
		}));
	}

	get wholeData() {
		return [...this.femaleData, ...this.maleData];
	}

	get descendingOrder() {
		return this.wholeData.sort((a, b) => (b?.group_value ?? 0) - (a?.group_value ?? 0));
	}

	get mainAudience() {
		return this.value?.audience?.main_audience ?? undefined;
	}

	getBarColor(
		audienceData: IGAudienceGroupDefault & { group_gender: 'M' | 'F' },
	): string {
		const firstData = this.descendingOrder[0];
		const secondData = this.descendingOrder[1];

		if (audienceData?.group_value === 0) return vars.global.colors.gray[60];

		if (
			audienceData?.group_name === firstData?.group_name &&
			audienceData.group_gender === firstData?.group_gender
		)
			return vars.global.colors.primary[60];
		else if (
			audienceData?.group_name === secondData?.group_name &&
			audienceData.group_gender === secondData?.group_gender
		)
			return vars.global.colors.gray[80];
		else return vars.global.colors.gray[60];
	}

	getBarChartItem(
		audienceData: IGAudienceGroupDefault & { group_gender: 'M' | 'F' },
	): SymmetricVerticalBarChartDataItem {
		return {
			barColor: this.getBarColor(audienceData),
			tooltip: `${
				audienceData?.group_value
					? roundFloat(audienceData?.group_value, 'down', 1)
					: ' - '
			}%`,
			valuePercentage:
				(80 / ((this.mainAudience?.main_group_rate ?? 0) * 100)) *
				(audienceData?.group_value ?? 0),
		};
	}

	audienceTextMapper: Record<string, string> = {
		'13-17': '13-17세',
		'18-24': '18-24세',
		'25-34': '25-34세',
		'35-44': '35-44세',
		'45-54': '45-54세',
		'55+': '55세 이상',
	};

	audienceGenderMapper: Record<string, string> = {
		M: '남성',
		F: '여성',
	};

	adapt(): AudienceProps {
		return {
			genderBarChart: {
				topData: this.femaleData.map((audienceData) =>
					this.getBarChartItem(
						audienceData as IGAudienceGroupDefault & { group_gender: 'M' | 'F' },
					),
				),
				bottomData: this.maleData.map((audienceData) =>
					this.getBarChartItem(
						audienceData as IGAudienceGroupDefault & { group_gender: 'M' | 'F' },
					),
				),
			},
			mainAudience: `${
				this.audienceTextMapper?.[this.mainAudience?.main_group_name ?? ''] ?? '-'
			} ${this.audienceGenderMapper?.[this.mainAudience?.main_group_gender ?? ''] ?? ''}`,
			mainAudiencePercentage: this.mainAudience?.main_group_rate
				? +roundFloat(this.mainAudience?.main_group_rate, 'down', 1)
				: 0,
		};
	}
}

export class AudienceLanguageAdapter {
	value: IGReportRes | YTReportRes | TTReportRes;

	constructor(obj: IGReportRes | YTReportRes | TTReportRes) {
		this.value = obj;
	}

	get mainLanguageName() {
		return this.value?.audience?.language_summary?.main_language_name ?? undefined;
	}

	get mainLanguageRate() {
		const rate = this.value?.audience?.language_summary?.main_language_rate;
		return rate ? roundFloat(rate, 'down', 1) : undefined;
	}

	get languageList() {
		return this.value?.audience?.language_summary?.language_list ?? [];
	}

	languageTextMapper: Record<string, string> = {
		Korean: '한국어',
		English: '영어',
		Japanese: '일본어',
		// Chinese: '중국어',
		// Spanish: '스페인어',
		// French: '프랑스어',
		// German: '독일어',
		// Russian: '러시아어',
		// Italian: '이탈리아어',
		// Arabic: '아랍어',
		// Thai: '태국어',
		// Vietnamese: '베트남어',
		// Filipino: '필리핀어',
	};

	getLanguageName(language?: string): string {
		if (!language) return '-';
		return this.languageTextMapper?.[language] ?? '기타';
	}

	getDataFormat(languageData: IGAudienceLanguageChild): HorizontalRateChartDataItem {
		return {
			label: this.getLanguageName(languageData?.name ?? undefined),
			value: `${languageData.value ? roundFloat(languageData.value, 'down', 1) : '-'}%`,
			isPositive: languageData.name === this.mainLanguageName,
			valuePercentage: languageData.value ?? undefined,
		};
	}

	get languageListData() {
		const dataList = this.languageList.map((languageData) =>
			this.getDataFormat(languageData),
		);
		return dataList.reduce<HorizontalRateChartDataItem[]>((acc, cur) => {
			if (cur.label === '기타') {
				const oldEtc = acc.find((item) => item.label === '기타');
				if (oldEtc) {
					if (!oldEtc.valuePercentage) oldEtc.valuePercentage = 0;
					const addedValuePercentage =
						oldEtc.valuePercentage + (cur.valuePercentage ?? 0);
					oldEtc.valuePercentage = addedValuePercentage;
					oldEtc.value = `${roundFloat(addedValuePercentage, 'down', 1)}%`;

					return [...acc.filter((item) => item.label !== '기타'), oldEtc];
				} else return [...acc, cur];
			} else return [...acc, cur];
		}, []);
	}

	adapt(): HorizontalRateChartProps {
		if (this.languageListData.length === 0) return { isError: true };

		return {
			desc: (
				<>
					계정의 주요 오디언스는 주로
					<br />
					<span>{`${this.getLanguageName(this.mainLanguageName)}(${
						this.mainLanguageRate ?? '-'
					}%)`}</span>
					사용합니다.
				</>
			),
			data: this.languageListData,
		};
	}
}

export class AudienceAdapter {
	value: IGReportRes | YTReportRes | TTReportRes;

	constructor(obj: IGReportRes | YTReportRes | TTReportRes) {
		this.value = obj;
	}

	get genderAdapter() {
		return new AudienceGenderAdapter(this.value);
	}

	get languageAdapter() {
		return new AudienceLanguageAdapter(this.value);
	}

	adapt(): AudienceProps {
		return {
			...this.genderAdapter.adapt(),
			languageRateChart: this.languageAdapter.adapt(),
		};
	}
}
